import { useMemo } from 'react';

import type { BaseRowData, LegacyDataProvider, LegacyDataProviderRowData } from '@adv-frontend/data-provider';
import {
    getCoreRowModel as getReactTableCoreRowModel,
    getExpandedRowModel as getReactTableExpandedRowModel,
} from '@tanstack/react-table';
import { isNumber, NormalizeUnion } from '@direct-frontend/stdlib';
import type { TableMeta, TableState } from '../types/tanstack-table-types';

import type { DataProviderGridRowData, GridColumnDefinition, GridTable } from '../types';

import { useReactTable } from './useReactTable';
import type { GridColumnSorting } from './useGridColumnSorting';
import type { GridColumnSizing } from './useGridColumnSizing';
import type { GridColumnPinning } from './useGridColumnPinning';
import type { GridColumnVisibility } from '../adapters/columnsVisibilityAdapter';
import type { GridColumnOrder } from '../adapters/columnsOrderAdapter';
import { GridRowsExpanded } from './useGridRowExpanded';

/**
 * Тип опций для грида.
 *
 * @typeParam RowData Данные строки.
 */
type GridOptions<RowData extends BaseRowData> = {
    /**
     * Описание колонок.
     */
    columns: GridColumnDefinition<RowData, string>[];
    /**
     * Объект с фиксированными колонками.
     */
    columnPinning?: GridColumnPinning;
    /**
     * Объект с размерами колонок.
     */
    columnSizing?: GridColumnSizing;
    /**
     * Объект с видимостью колонок.
     */
    columnVisibility?: GridColumnVisibility;
    /**
     * Объект с порядком колонок.
     */
    columnOrder?: GridColumnOrder;
    /**
     * Массив с объектами для сортировки и сеттером для нее.
     */
    columnSorting?: GridColumnSorting;
    /**
     * Дефолтные параметры для всех столбцов.
     */
    defaultColumn?: Partial<GridColumnDefinition<RowData, string>>;
    /**
     * Контекст для ячеек таблицы.
     */
    meta?: TableMeta<RowData>;
    /**
     * Параметры сортировки грида.
     */
    sortingOptions?: GridSortingOptions;
    /**
     * Параметры развернутых строк грида.
     */
    rowsExpanded?: GridRowsExpanded;
} & NormalizeUnion<
    | {
          /**
           * Способ дозагрузки строк (по скроллу до последней строки или по кнопке "загрузить еще")
           */
          loadMoreRowsType: 'onClick';
          /**
           * Количество строк, добавляемых при нажатии на кнопку "загрузить еще"
           */
          rowsChunkSize: number;
          /**
           * Кол-во отображаемых строк на гриде (для контроля при дозагрузке по кнопке)
           */
          rowsCount: number;
          /**
           * Кол-во отображаемых подстрок на гриде (для контроля при дозагрузке по кнопке)
           */
          subRowsCount: Record<string, number>;
      }
    | {
          /**
           * Способ дозагрузки строк (по скроллу до последней строки или по кнопке "загрузить еще")
           */
          loadMoreRowsType: 'onScroll';
      }
>;

/**
 * Параметры сортировки грида
 */
export interface GridSortingOptions {
    /**
     * Включает ручную сортировку для таблицы
     * true - в таблицу приходят заранее отсортированные данные
     * false - сортировка происходит нативными средствами таблицы
     */
    manualSorting?: boolean;
}

const getRowId = (originalRow: LegacyDataProviderRowData) => originalRow.id;
const getSubRows = (row: LegacyDataProviderRowData) => row.subRows;
const getCoreRowModel = getReactTableCoreRowModel();
const getExpandedRowModel = getReactTableExpandedRowModel();

/**
 * Хук для работы с гридом.
 *
 * @param dataProvider Датапровайдер.
 * @param options Опции грида.
 * @typeParam RowData Данные строки.
 * @returns Необходимые данные для грида.
 */
export function useGrid<RowData extends BaseRowData>(
    dataProvider: LegacyDataProvider<RowData>,
    options: GridOptions<RowData>
): GridTable<RowData> {
    const {
        columnOrder,
        columnPinning,
        columns,
        columnSizing,
        columnSorting,
        columnVisibility,
        defaultColumn,
        meta,
        rowsExpanded,
        sortingOptions,
    } = options;
    const { getRows } = dataProvider;

    const defaultColumnSize = columnSizing?.columnSizing['*'];
    const defaultColumnWithDefaults = useMemo(
        () => ({
            hasDelimiter: true,
            ...(isNumber(defaultColumnSize) && { size: defaultColumnSize }),
            ...(defaultColumn ?? {}),
        }),
        [defaultColumn, defaultColumnSize]
    );

    const tableState = useMemo<Partial<TableState>>(
        () => ({
            ...(columnSorting && { sorting: columnSorting.columnSorting }),
            ...(columnSizing && { columnSizing: columnSizing.columnSizing }),
            ...(columnPinning && { columnPinning: columnPinning.columnPinning }),
            ...(columnVisibility && { columnVisibility: columnVisibility.columnVisibility }),
            ...(columnOrder && { columnOrder: columnOrder.columnOrder }),
            ...(rowsExpanded && { expanded: rowsExpanded.rowsExpanded }),
        }),
        [columnOrder, columnPinning, columnSizing, columnSorting, columnVisibility, rowsExpanded]
    );

    // Маппинг происходит здесь, т.к. иначе не будет обновления данных при дозагрузке строк
    const tableData = useMemo<DataProviderGridRowData[]>(() => {
        let rows: DataProviderGridRowData[] = getRows();

        if (options.loadMoreRowsType === 'onScroll') {
            return rows;
        }

        const totalCount = rows.length;
        const currentCount = options.rowsCount;
        const remainingCount = totalCount - currentCount;
        if (remainingCount > 0) {
            rows = rows.slice(0, currentCount);
            rows.push({
                id: 'loadMore',
                type: 'loadMore',
                status: 'loaded',
            });
        }

        function mapSubRows(row: DataProviderGridRowData): DataProviderGridRowData[] | undefined {
            if (row.type === 'loadMore') {
                return undefined;
            }

            const originalRows = row.subRows;
            if (!originalRows || originalRows.length === 0 || options.loadMoreRowsType === 'onScroll') {
                return originalRows;
            }

            const currentCount = options.subRowsCount[row.id] ?? options.rowsChunkSize;
            const shouldTrim = originalRows.length > currentCount;
            const visibleRows = shouldTrim ? originalRows.slice(0, currentCount) : originalRows;

            let changed = shouldTrim;
            let nextRows = visibleRows;

            for (let i = 0; i < visibleRows.length; i++) {
                const subRow = visibleRows[i];
                if (subRow?.subRows === undefined) {
                    continue;
                }

                const mappedSubRows = mapSubRows(subRow);
                if (mappedSubRows === subRow.subRows) {
                    continue;
                }

                if (!changed) {
                    changed = true;
                    nextRows = visibleRows.slice();
                }
                nextRows[i] = { ...subRow, subRows: mappedSubRows };
            }

            if (shouldTrim) {
                nextRows.push({
                    id: `${row.id}.loadMore`,
                    type: 'loadMore',
                    status: 'loaded',
                });
            }

            return changed ? nextRows : originalRows;
        }

        return rows.map(row => {
            if (row.subRows === undefined) {
                return row;
            }
            const mappedRows = mapSubRows(row);
            if (mappedRows === row.subRows) {
                return row;
            }

            return { ...row, subRows: mappedRows };
        });
    }, [getRows, options.loadMoreRowsType, options.rowsChunkSize, options.rowsCount, options.subRowsCount]);

    return useReactTable({
        columns,
        defaultColumn: defaultColumnWithDefaults,
        columnResizeMode: 'onEnd',
        data: tableData,
        meta,
        enableColumnResizing: Boolean(columnSizing),
        getCoreRowModel,
        getRowId,
        enableSortingRemoval: false,
        ...sortingOptions,
        ...(rowsExpanded && { getSubRows, getExpandedRowModel }),
        state: tableState,
        ...(columnSorting && { onSortingChange: columnSorting.setColumnSorting }),
        ...(columnSizing && { onColumnSizingChange: columnSizing.setColumnSizing }),
        ...(columnVisibility && { onColumnVisibilityChange: columnVisibility.setColumnVisibility }),
        ...(columnOrder && { onColumnOrderChange: columnOrder.setColumnOrder }),
        ...(rowsExpanded && { onExpandedChange: rowsExpanded.setRowsExpanded }),
    });
}
